import React, { useState, useEffect, useMemo } from 'react';
import HealthDot, { HEALTH_IN_PROGRESS } from './ui/HealthDot.jsx';
import StatusPill from './ui/StatusPill.jsx';
import AvatarStack from './ui/AvatarStack.jsx';
import SearchIcon from './ui/SearchIcon.jsx';
import HeaderUserChip from './ui/HeaderUserChip.jsx';
import HeaderBrand from './ui/HeaderBrand.jsx';
import SyncTimestamp from './ui/SyncTimestamp.jsx';
import Skeleton from './ui/Skeleton.jsx';
import { fmtHours } from '../utils/formatters.js';

export default function ProjectsListPage({
  current_user = null,
  // Optional server-embedded initial data (used by specs / SSR). When absent the
  // list is fetched from /projects.json after mount so the shell paints instantly.
  projects: initialProjects = null,
  managers_index: initialManagers = [],
  fetched_at: initialFetched = null
}) {
  const [loadStatus, setLoadStatus]       = useState(initialProjects ? 'loaded' : 'loading');
  const [projects, setProjects]           = useState(initialProjects || []);
  const [managersIndex, setManagersIndex] = useState(initialManagers || []);
  const [fetchedAt, setFetchedAt]         = useState(initialFetched);

  const [query, setQuery]                  = useState('');
  // Default to Active: the board is about running work; Paused (no time this or
  // last month) is one click away via the All/Paused chips.
  const [statusFilter, setStatusFilter]    = useState('Active');
  const [healthFilter, setHealthFilter]    = useState('All');
  const [managerFilter, setManagerFilter]  = useState('all');
  const [view, setView]                    = useState('table');
  const [sort, setSort]                    = useState({ key: 'hours_mtd', dir: 'desc' });
  const [hoverHealth, setHoverHealth]      = useState(null);

  // Load the (Redmine-backed) project list after mount. The cached server payload
  // makes warm hits fast; cold hits no longer block the first paint.
  function loadList() {
    setLoadStatus('loading');
    fetch('/projects.json', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((res) => {
        if (res.status === 401) { window.location = '/session/new'; return null; }
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((data) => {
        if (!data) return;
        setProjects(data.projects || []);
        setManagersIndex(data.managers_index || []);
        setFetchedAt(data.fetched_at || null);
        setLoadStatus('loaded');
      })
      .catch(() => setLoadStatus('error'));
  }

  useEffect(() => { if (loadStatus !== 'loaded') loadList(); }, []);

  const loading = loadStatus === 'loading';
  const errored = loadStatus === 'error';

  const filtered = useMemo(() => {
    const STATUS_LABEL = { active: 'Active', paused: 'Paused' };
    const r = projects.filter(p => {
      if (query && !p.name.toLowerCase().includes(query.toLowerCase())) return false;
      if (statusFilter !== 'All' && STATUS_LABEL[p.status] !== statusFilter) return false;
      if (managerFilter !== 'all' &&
          !(p.managers || []).some(m => String(m.id) === String(managerFilter))) return false;
      if (healthFilter === 'At risk' && p.health >= 50) return false;
      if (healthFilter === 'Healthy' && p.health < 75) return false;
      return true;
    });
    r.sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      const k = sort.key;
      if (k === 'project') return String(a.name).localeCompare(String(b.name)) * dir;
      if (k === 'status')  return String(a.status).localeCompare(String(b.status)) * dir;
      return ((a[k] ?? 0) - (b[k] ?? 0)) * dir;
    });
    return r;
  }, [projects, query, statusFilter, healthFilter, managerFilter, sort]);

  const toggleSort = (key) =>
    setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });

  const SortHeader = ({ label, k, align = 'left' }) => (
    <button
      onClick={() => toggleSort(k)}
      className="flex items-center gap-1 hover:text-[#1A1A1A] transition-colors"
      style={{
        color: sort.key === k ? '#1A1A1A' : '#6B6B68',
        textAlign: align,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        width: '100%',
        background: 'none', border: 'none', padding: 0, cursor: 'pointer'
      }}
    >
      {label}
      {sort.key === k && <span style={{ fontSize: 9 }}>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
    </button>
  );

  const Chip = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className="px-2.5 py-1 text-[12px] rounded-full border transition-colors"
      style={{
        borderColor: active ? '#1A1A1A' : '#E0DFD9',
        background:  active ? '#1A1A1A' : '#fff',
        color:       active ? '#fff'    : '#3A3A38',
        fontWeight:  active ? 500       : 400,
        cursor: 'pointer'
      }}
    >{children}</button>
  );

  const atRisk = projects.filter(p => p.health < 50).length;

  return (
    <div style={{ background: '#FAFAF7', minHeight: '100vh' }}>
      <header className="border-b" style={{ background: '#fff', borderColor: '#ECEBE5' }}>
        <div className="max-w-[1440px] mx-auto px-8 h-14 flex items-center justify-between">
          <HeaderBrand />
          <HeaderUserChip user={current_user} />
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-8 pt-8 pb-24">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-[28px] font-semibold tracking-tight" style={{ color: '#1A1A1A', letterSpacing: '-0.02em' }}>
              Projects
            </h1>
            <p className="text-[13px] mt-1 flex items-center gap-2 flex-wrap" style={{ color: '#6B6B68' }}>
              {loading && <Skeleton width={230} height={13} />}
              {errored && (
                <span style={{ color: '#9B1F26' }}>
                  Couldn't load projects —{' '}
                  <button
                    type="button"
                    onClick={loadList}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#9B1F26', textDecoration: 'underline', cursor: 'pointer' }}
                  >Retry</button>
                </span>
              )}
              {!loading && !errored && (
                <>
                  <span>{filtered.length} of {projects.length} projects · {atRisk} need attention</span>
                  <span style={{ color: '#D8D6CE' }}>·</span>
                  <SyncTimestamp fetchedAt={fetchedAt} />
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border" style={{ borderColor: '#E0DFD9', background: '#fff' }}>
              {['table', 'grid'].map(v => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className="px-3 py-1.5 text-[12px] capitalize"
                  style={{
                    background: view === v ? '#F2F1ED' : 'transparent',
                    color:      view === v ? '#1A1A1A' : '#6B6B68',
                    fontWeight: view === v ? 500       : 400,
                    border: 'none', cursor: 'pointer'
                  }}
                >{v}</button>
              ))}
            </div>
            <div
              className="inline-flex items-center gap-1.5 text-[11.5px] px-2.5 py-1.5 rounded-md border"
              style={{ borderColor: '#E0DFD9', background: '#fff', color: '#6B6B68' }}
            >
              <span style={{ width: 6, height: 6, borderRadius: 99, background: '#2F8F5A', display: 'inline-block' }} />
              Synced from Redmine
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="relative">
            <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9A9A95' }}>
              <SearchIcon />
            </div>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search projects or clients…"
              className="text-[12px] py-1.5 pr-3 rounded-md border focus:outline-none"
              style={{ paddingLeft: 30, width: 280, borderColor: '#E0DFD9', background: '#fff', color: '#1A1A1A' }}
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wider" style={{ color: '#9A9A95' }}>Status</span>
            {['All', 'Active', 'Paused'].map(s => (
              <Chip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)}>{s}</Chip>
            ))}
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wider" style={{ color: '#9A9A95' }}>Health</span>
            {['All', 'At risk', 'Healthy'].map(s => (
              <Chip key={s} active={healthFilter === s} onClick={() => setHealthFilter(s)}>{s}</Chip>
            ))}
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wider" style={{ color: '#9A9A95' }}>Manager</span>
            <select
              value={managerFilter}
              onChange={(e) => setManagerFilter(e.target.value)}
              className="text-[12px] py-1 px-2 rounded-md border focus:outline-none"
              style={{ borderColor: '#E0DFD9', background: '#fff', color: '#3A3A38' }}
            >
              <option value="all">All managers</option>
              {managersIndex.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>

        {loading && renderSkeletonTable()}
        {!loading && !errored && view === 'table' && renderTable({ filtered, hoverHealth, setHoverHealth, SortHeader })}
        {!loading && !errored && view === 'grid'  && renderGrid({ filtered, hoverHealth, setHoverHealth })}
      </main>
    </div>
  );
}

function renderSkeletonTable() {
  const cols = [110, 280, 110, 150, 150, 90];
  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: '#ECEBE5', background: '#fff' }}>
      <table className="w-full text-left" style={{ fontSize: 13 }}>
        <thead style={{
          background: '#FBFBF8',
          fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em',
          color: '#9A9A95', fontWeight: 500
        }}>
          <tr style={{ borderBottom: '1px solid #ECEBE5' }}>
            <th className="py-3 pl-5 pr-2" style={{ width: cols[0] }}>Health</th>
            <th className="py-3 px-3"      style={{ width: cols[1] }}>Project</th>
            <th className="py-3 px-3"      style={{ width: cols[2] }}>Status</th>
            <th className="py-3 px-3"      style={{ width: cols[3] }}>Team</th>
            <th className="py-3 px-3"      style={{ width: cols[4] }}>Hours (MTD)</th>
            <th className="py-3 px-3 pr-5" style={{ width: cols[5] }}>Managers</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 8 }).map((_, i) => (
            <tr key={i} style={{ borderBottom: i === 7 ? 'none' : '1px solid #F0EFE9' }}>
              <td className="py-3 pl-5 pr-2"><Skeleton width={42}  height={12} /></td>
              <td className="py-3 px-3"><Skeleton width={180} height={12} /></td>
              <td className="py-3 px-3"><Skeleton width={56}  height={20} radius={999} /></td>
              <td className="py-3 px-3"><Skeleton width={96}  height={22} radius={999} /></td>
              <td className="py-3 px-3 text-right"><Skeleton width={40} height={12} /></td>
              <td className="py-3 px-3 pr-5"><Skeleton width={50} height={22} radius={999} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Column-header legend for the Status column — explains Active/Paused once here
// (an info icon with a hover tooltip) instead of repeating a tooltip on every row.
function StatusLegend() {
  const [open, setOpen] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <svg width="13" height="13" viewBox="0 0 16 16" aria-label="What Active and Paused mean" style={{ display: 'block' }}>
        <circle cx="8" cy="8" r="7" fill="none" stroke="#9A9A95" strokeWidth="1.2" />
        <text x="8" y="11.7" textAnchor="middle" fontSize="10" fontStyle="italic" fontWeight="700" fontFamily="Georgia, serif" fill="#9A9A95">i</text>
      </svg>
      {open && (
        <span
          role="tooltip"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50,
            width: 272, background: '#1A1A1A', color: '#fff',
            fontSize: 11, fontWeight: 400, lineHeight: 1.5,
            textTransform: 'none', letterSpacing: 'normal',
            padding: '8px 10px', borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            whiteSpace: 'normal', pointerEvents: 'none'
          }}
        >
          <span style={{ display: 'block' }}>
            <span style={{ color: '#9FD3B0', fontWeight: 600 }}>Active</span> — time has been logged this month or last month.
          </span>
          <span style={{ display: 'block', marginTop: 4 }}>
            <span style={{ color: '#E2C893', fontWeight: 600 }}>Paused</span> — no time logged this month or last month, so the project is treated as inactive.
          </span>
        </span>
      )}
    </span>
  );
}

function renderTable({ filtered, hoverHealth, setHoverHealth, SortHeader }) {
  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: '#ECEBE5', background: '#fff' }}>
      <div style={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
        <table
          className="w-full text-left"
          style={{ fontSize: 13, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}
        >
          <thead style={{
            position: 'sticky', top: 0, zIndex: 5,
            background: '#FBFBF8',
            fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em',
            color: '#9A9A95', fontWeight: 500
          }}>
            <tr style={{ borderBottom: '1px solid #ECEBE5' }}>
              <th className="py-3 pl-5 pr-2" style={{ width: 110 }}><SortHeader label="Health"     k="health" /></th>
              <th className="py-3 px-3"      style={{ width: 280 }}><SortHeader label="Project"    k="project" /></th>
              <th className="py-3 px-3"      style={{ width: 110 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <SortHeader label="Status" k="status" />
                  <StatusLegend />
                </div>
              </th>
              <th className="py-3 px-3"      style={{ width: 150 }}>Team</th>
              <th className="py-3 px-3"      style={{ width: 150 }}><SortHeader label="Hours (MTD)"  k="hours_mtd"  align="right" /></th>
              <th className="py-3 px-3 pr-5" style={{ width: 90  }}>Managers</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, idx) => {
              const team = (p.team || []).length ? p.team : [];
              return (
                <tr
                  key={p.id}
                  onClick={() => { window.location.href = `/projects/${p.id}`; }}
                  className="cursor-pointer transition-colors"
                  style={{
                    borderBottom: idx === filtered.length - 1 ? 'none' : '1px solid #F0EFE9',
                    background: '#fff'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FBFBF8'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <td className="py-3 pl-5 pr-2">
                    <div
                      className="flex items-center gap-2 relative"
                      style={{ cursor: 'default' }}
                      onMouseEnter={() => setHoverHealth(p.id)}
                      onMouseLeave={() => setHoverHealth(null)}
                    >
                      <HealthDot score={p.health} size={9} disabled />
                      <span style={{ color: '#9A9A95', fontWeight: 600 }}>{p.health}</span>
                      {hoverHealth === p.id && (
                        <div
                          role="tooltip"
                          className="absolute z-50"
                          style={{
                            top: 'calc(100% + 6px)', left: 0,
                            background: '#1A1A1A', color: '#fff',
                            borderRadius: 6, padding: '8px 10px',
                            fontSize: 11, maxWidth: 240, lineHeight: 1.5,
                            whiteSpace: 'normal', pointerEvents: 'none',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
                          }}
                        >
                          {HEALTH_IN_PROGRESS}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <div className="font-medium" style={{ color: '#1A1A1A' }}>{p.name}</div>
                  </td>
                  <td className="py-3 px-3"><StatusPill status={p.status} showHint={false} /></td>
                  <td className="py-3 px-3">
                    <AvatarStack engineers={team} max={5} size={22} />
                  </td>
                  <td className="py-3 px-3 text-right">
                    <span style={{ minWidth: 38 }}>{fmtHours(p.hours_mtd)}</span>
                  </td>
                  <td className="py-3 px-3 pr-5">
                    {(p.managers || []).length === 0
                      ? <span style={{ color: '#9A9A95', fontSize: 14 }}>—</span>
                      : <AvatarStack engineers={p.managers} max={3} size={22} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function renderGrid({ filtered, hoverHealth, setHoverHealth }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      {filtered.map(p => {
        const team = p.team || [];
        return (
          <button
            key={p.id}
            onClick={() => { window.location.href = `/projects/${p.id}`; }}
            className="text-left rounded-lg border p-4 transition-colors hover:bg-[#FBFBF8]"
            style={{ background: '#fff', borderColor: '#ECEBE5', cursor: 'pointer' }}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <div className="font-medium text-[14px]" style={{ color: '#1A1A1A' }}>{p.name}</div>
              </div>
              <StatusPill status={p.status} />
            </div>
            <div className="flex items-center gap-3 mt-3 mb-3">
              <div
                className="flex items-center gap-1.5 relative"
                style={{ cursor: 'default' }}
                onMouseEnter={() => setHoverHealth(p.id)}
                onMouseLeave={() => setHoverHealth(null)}
              >
                <HealthDot score={p.health} disabled />
                <span style={{ color: '#9A9A95', fontWeight: 600, fontSize: 13 }}>{p.health}</span>
                {hoverHealth === p.id && (
                  <div
                    role="tooltip"
                    className="absolute z-50"
                    style={{
                      top: 'calc(100% + 6px)', left: 0,
                      background: '#1A1A1A', color: '#fff',
                      borderRadius: 6, padding: '6px 10px',
                      fontSize: 11, whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
                    }}
                  >
                    {HEALTH_IN_PROGRESS}
                  </div>
                )}
              </div>
              <div className="ml-auto"><AvatarStack engineers={team} max={4} size={20} /></div>
            </div>
            <div className="mt-3 pt-3 border-t" style={{ borderColor: '#F0EFE9' }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#9A9A95', letterSpacing: '0.04em' }}>Hours MTD</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{fmtHours(p.hours_mtd)}</div>
            </div>
            {(p.managers || []).length > 0 && (
              <div className="mt-3 flex items-center gap-2 text-[11px]" style={{ color: '#6B6B68' }}>
                <span style={{ textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9A9A95' }}>Managers</span>
                <AvatarStack engineers={p.managers} max={3} size={20} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
